/**
 * Pure team-access rules shared by AccountStore, the fetch interceptor, the API
 * client and the UI. Deliberately free of MobX / React / service imports so the
 * interceptor can use it without an import cycle and it can be unit-tested alone.
 *
 * Contract: backend-ai docs/DRIVER_ACCESS_AND_LOAD_SYNC.md §3–§5.
 */
import type { AccountMembership, ClientAccess } from "@services/teamService";

/** Value this dashboard sends in the `X-Client` header on every API call. */
export const CLIENT_HEADER = "X-Client";
export const CLIENT_NAME = "web";

/** 403 codes the backend uses for the driver-access rules. */
export const CLIENT_NOT_ALLOWED = "CLIENT_NOT_ALLOWED";
export const FIELDS_NOT_ALLOWED = "FIELDS_NOT_ALLOWED";

const ACCESS_ERROR_CODES = new Set([CLIENT_NOT_ALLOWED, FIELDS_NOT_ALLOWED]);

/**
 * Pull one of the access codes above out of an error body. The backend's error
 * handler puts a machine-readable reason in `errorCode`, while plain
 * `c.json({ error })` responses carry it as the message itself, so both are read.
 * (`code` is the numeric HTTP status in this backend, hence the string check.)
 */
export function accessErrorCode(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  for (const key of ["errorCode", "code", "error", "message"]) {
    const v = b[key];
    if (typeof v === "string" && ACCESS_ERROR_CODES.has(v)) return v;
  }
  return null;
}

/** Missing on older backends: treat as the default, "web dashboard + driver app". */
export function clientAccessOf(m: { clientAccess?: ClientAccess | null }): ClientAccess {
  return m.clientAccess ?? "all";
}

/**
 * Whether the web dashboard may act inside this account. The user's own account
 * is never restricted; a membership whose role is "Driver app only" is.
 */
export function isWebAllowed(m: AccountMembership): boolean {
  if (m.isSelf || m.role === "owner") return true;
  return clientAccessOf(m) !== "app";
}

/** The account list as the web switcher should see it. */
export function webMemberships(list: AccountMembership[]): AccountMembership[] {
  return list.filter(isWebAllowed);
}

/**
 * Resolve which account to act in at boot. Falls back to the user's own account
 * when nothing is stored, when the stored account is gone, or when its role no
 * longer allows the web dashboard.
 */
export function resolveActiveAccountId(
  stored: string | null,
  selfId: string,
  memberships: AccountMembership[]
): string {
  if (!stored || stored === selfId) return selfId;
  const m = memberships.find((x) => x.accountId === stored);
  return m && isWebAllowed(m) ? stored : selfId;
}

/**
 * Fields & yield maps are visible unless the role explicitly turns them off.
 * `undefined` (older backend, or the user's own account) means visible.
 */
export function canViewFieldsIn(m: AccountMembership | null | undefined): boolean {
  if (!m || m.isSelf) return true;
  return m.canViewFields !== false;
}
